import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { linkStyle } from "@repo/ui/Link/index";
import { useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";

type FieldView = "edit" | "diff";

interface FieldDiffState {
  /** The field's value differs from what it is diffed against. */
  hasChanges: boolean;
  /** `diff` only while the toggle is on and there is something to compare. */
  view: FieldView;
  onToggle: () => void;
}

/**
 * Tracks whether a field is showing its editor or a diff against the value it
 * was changed from. The toggle resets once the two match — after a save, a
 * cancel, or a discarded suggestion — so the next edit starts in the editor
 * rather than in a diff nobody asked for.
 */
function useFieldDiffState(
  savedValue: string | undefined,
  value: string,
): FieldDiffState {
  const [view, setView] = useState<FieldView>("edit");
  const hasChanges = savedValue !== undefined && savedValue !== value;
  if (!hasChanges && view === "diff") {
    setView("edit");
  }
  return {
    hasChanges,
    view: hasChanges ? view : "edit",
    onToggle: () => {
      setView((current) => (current === "edit" ? "diff" : "edit"));
    },
  };
}

interface FieldDiffToggleProps {
  view: FieldView;
  onToggle: () => void;
}

/** Switches a field between its editor and a diff against the saved value. */
const FieldDiffToggle: FC<FieldDiffToggleProps> = (props) => {
  const { view, onToggle } = props;
  return (
    <Button
      variant={ButtonVariant.Tertiary}
      size={Size.MEDIUM}
      className={twMerge("ml-auto cursor-pointer pr-0", linkStyle())}
      onClick={onToggle}
    >
      {view === "edit" ? "See changes" : "Edit"}
    </Button>
  );
};

export interface DiffableFieldProps {
  label: ReactNode;
  /** The field's current value. */
  value: string;
  /** What "See changes" diffs the value against. Absent where there is nothing to compare to. */
  savedValue: string | undefined;
  /**
   * A pending Ask Alpharun suggestion for the field. While it is set, the
   * field shows the diff from its value to the suggestion, with no toggle, and
   * the caller disables its editor.
   */
  suggestedValue?: string;
  /** Sizing for the diff view, matched to the editor it replaces. */
  diffClassName?: string;
  children: ReactNode;
}

/**
 * A labeled field that can show what changed: its editor by default, and a
 * word diff against `savedValue` while "See changes" is on. The toggle only
 * appears once the two differ, so an untouched field carries no extra chrome.
 * A suggestion replaces the toggle with a diff of what the suggestion changes.
 *
 * The editor is the caller's — an input, a textarea, anything text-shaped —
 * so the diff view fits any form field.
 */
export const DiffableField: FC<DiffableFieldProps> = (props) => {
  const { label, value, savedValue, suggestedValue, diffClassName, children } =
    props;
  // A suggestion is diffed from the value, and the value from what was saved.
  const diffFrom = suggestedValue === undefined ? savedValue : value;
  const diffTo = suggestedValue ?? value;
  const diff = useFieldDiffState(diffFrom, diffTo);
  // A suggestion opens on its diff; otherwise the diff waits for the toggle.
  const showsDiff =
    suggestedValue === undefined ? diff.view === "diff" : diff.hasChanges;

  return (
    <>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        {label}
        {suggestedValue === undefined && diff.hasChanges ? (
          <FieldDiffToggle view={diff.view} onToggle={diff.onToggle} />
        ) : null}
      </div>
      {showsDiff && diffFrom !== undefined ? (
        <FieldDiffView
          className={diffClassName}
          savedValue={diffFrom}
          editedValue={diffTo}
        />
      ) : (
        children
      )}
    </>
  );
};
